/**
 * Shared single-line text input. Before this, form inputs were
 * hand-rolled per screen — heights ranged h-10…h-16, radii lg/xl, and
 * backgrounds split between bg-card and bg-paper. This unifies the
 * standard form field: 44px tall, rounded-xl, card surface, oxblood
 * focus border.
 *
 * Forwards a ref and all native input props (type, value, onChange,
 * placeholder, inputMode, maxLength, …). Pass `mono` for codes/IDs.
 * The large display inputs on the auth screens (OTP/PIN, with wide
 * letter-spacing) stay bespoke — they're a deliberate hero treatment,
 * not a standard field.
 */
import { forwardRef, type InputHTMLAttributes } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Monospace + slightly smaller — for invite codes, timezones, IDs. */
  mono?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { mono = false, className = '', ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={`w-full h-11 px-3 rounded-xl border border-line bg-card text-ink placeholder:text-muted focus:outline-none focus:border-oxblood ${
        mono ? 'font-mono text-sm' : ''
      } ${className}`}
      {...rest}
    />
  );
});
